import React, { useEffect, useState } from 'react';
import { Button } from 'antd';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import AppShell from '../Components/AppShell';
import Container from '../Components/Container';
import {
    BusinessHoursIcon,
    EmailIcon,
    LiveChatIcon,
    PhoneIcon,
    SupportIcon,
} from '../Components/icons';
import axiosInstance from '../utils/axiosInstance';

/** Props every contact icon is called with, mirroring the icons module. */
type ContactIcon = React.FC<{ 'aria-hidden'?: boolean | 'true' | 'false' }>;

interface ContactCard {
    key: string;
    Icon: ContactIcon;
    labelKey: string;
    value: string;
    /** Absent for business hours — it is information, not an action. */
    href?: string;
}

const SupportPage: React.FC = () => {

    const { t } = useTranslation();
    const [settings, setSettings] = useState({
        supportPhone: '+65 1111 1118',
        supportEmail: 'support@bonia.com.sg',
        liveChatHours: ''
    });

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                // Adjust route to match the unauthenticated settings route
                const response = await axiosInstance.get('/auth/settings');
                if (response.data && response.data.data) {
                    setSettings({
                        supportPhone: response.data.data.supportPhone,
                        supportEmail: response.data.data.supportEmail,
                        liveChatHours: response.data.data.liveChatHours ?? ''
                    });
                }
            } catch (error) {
                console.error("Failed to fetch settings", error);
            }
        };
        fetchSettings();
    }, []);

    const supportClick = () => {
        Swal.fire({
            text: t("please_reach_support"),
            icon: 'info',
        })
    }

    /**
     * The three contact details, in display order.
     *
     * Declared as data rather than three hand-written blocks so the grid
     * reflows and the card treatment is written once. Phone and email carry
     * an `href`; business hours deliberately does not, so it renders as a
     * plain card with no hover or focus affordance — nothing to click.
     */
    const CONTACTS: ContactCard[] = [
        {
            key: 'hours',
            Icon: BusinessHoursIcon,
            labelKey: 'business_hours',
            // Admin-configured hours (Support Contact settings); falls back to the
            // translated default when the setting has not been filled in.
            value: settings.liveChatHours || t('business_hours_value'),
        },
        {
            key: 'phone',
            Icon: PhoneIcon,
            labelKey: 'call_us',
            value: settings.supportPhone,
            href: `tel:${settings.supportPhone}`,
        },
        {
            key: 'email',
            Icon: EmailIcon,
            labelKey: 'email_us',
            value: settings.supportEmail,
            href: `mailto:${settings.supportEmail}`,
        },
    ];

    // Shared between the <a> and <div> branches below so the two card kinds
    // are identical apart from the interaction styling.
    const cardBase =
        'flex items-center gap-4 rounded-md border border-solid border-brand-300 bg-white ' +
        'px-4 py-4 text-left lg:flex-col lg:items-center lg:text-center lg:gap-3 lg:py-6';

    return (
        <AppShell>
            {/* BG-014 is an 800x900 portrait, so it was being squashed into this
                landscape box: `object-cover` was scoped to lg, leaving mobile with
                no object-fit at all and the image stretched to the box's shape.

                `object-top` because the subject's head sits flush against the top
                edge of the source — with the default centre position, cropping to a
                short landscape strip cuts through the forehead. Anchoring to the top
                keeps the head and headset whole and drops the shoulders instead, and
                it improves the desktop crop for the same reason. The mobile box is a
                little taller than 240px so the crop is less severe on wide phones. */}
            <div className='relative w-full h-[280px] lg:h-[420px]'>
                <img src='/images/BG-014.png' className='w-full h-full object-cover object-top' alt="" />
                {/* Gradient rather than a flat scrim: the title sits on the darkest
                    part while the image keeps its detail at the top. */}
                <div className='hidden lg:flex absolute inset-0 items-center justify-center bg-gradient-to-t from-brand-900/70 via-brand-900/35 to-brand-900/10'>
                    <div className='flex flex-col items-center gap-5'>
                        <h1 className='font-display text-display-md text-white m-0 tracking-wide'>
                            {t("customer_service")}
                        </h1>
                        <span className='h-px w-16 bg-accent-500' aria-hidden='true' />
                    </div>
                </div>
            </div>

            {/* Mobile title. Playfair + accent rule, so it echoes the desktop hero
                treatment instead of being a bold sans heading on its own. */}
            <div className='lg:hidden flex flex-col items-center gap-3 pt-8'>
                <h2 className='m-0 text-center font-display text-[25px] text-brand-900'>
                    {t("customer_service")}
                </h2>
                <span className='h-px w-12 bg-accent-500' aria-hidden='true' />
            </div>

            <Container>
                <div className='px-4 pb-8 lg:py-12'>
                    <div className='lg:mx-auto lg:max-w-[640px]'>
                        <div className='mx-auto mt-6 max-w-[48ch] text-[18px] text-center text-brand-600 leading-relaxed lg:mt-0 lg:leading-loose'>
                            {t("support_message")}
                        </div>

                        <div className='w-full flex justify-center mt-8'>
                            <Button
                                className={
                                    'w-[80%] text-white bg-brand-800 uppercase tracking-[0.12em] ' +
                                    'lg:w-full lg:max-w-[280px] lg:tracking-[0.16em] ' +
                                    // `!` so the accent hover wins over antd's own
                                    // derived primary-hover colour deterministically.
                                    'hover:!bg-accent-600 hover:!border-accent-600 ' +
                                    'focus-visible:outline focus-visible:outline-2 ' +
                                    'focus-visible:outline-offset-2 focus-visible:outline-accent-500'
                                }
                                size='large'
                                type="primary"
                                icon={<LiveChatIcon aria-hidden='true' />}
                                onClick={() => {
                                    supportClick();
                                }}>
                                {t("live_chat")}
                            </Button>
                        </div>
                    </div>

                    {/* Contact details. Wider than the copy column above so the three
                        cards get room to sit side by side without cramping the email. */}
                    <div className='mt-12 lg:mx-auto lg:mt-16 lg:max-w-[860px]'>
                        {/* Ornamental divider — separates the two halves of the page
                            without inventing a heading that would need translating. */}
                        <div className='flex items-center justify-center gap-4 mb-8'>
                            <span className='h-px w-10 bg-brand-300' aria-hidden='true' />
                            <SupportIcon className='text-brand-300' aria-hidden='true' />
                            <span className='h-px w-10 bg-brand-300' aria-hidden='true' />
                        </div>

                        <div className='grid grid-cols-1 gap-3 lg:grid-cols-3 lg:gap-4'>
                            {CONTACTS.map(({ key, Icon, labelKey, value, href }) => {
                                const body = (
                                    <>
                                        <span
                                            className={
                                                'flex h-12 w-12 shrink-0 items-center justify-center rounded-full ' +
                                                'bg-brand-100 text-brand-800 text-[18px] transition-colors duration-150 ' +
                                                'group-hover:bg-accent-500/15 group-hover:text-accent-600'
                                            }
                                        >
                                            <Icon aria-hidden='true' />
                                        </span>
                                        <span className='flex min-w-0 flex-col'>
                                            <span className='text-xs uppercase tracking-[0.16em] text-brand-600'>
                                                {t(labelKey)}
                                            </span>
                                            <span className='mt-1 text-brand-900 break-words'>
                                                {value}
                                            </span>
                                        </span>
                                    </>
                                );

                                return href ? (
                                    <a
                                        key={key}
                                        href={href}
                                        className={
                                            'group no-underline transition-all duration-150 ' +
                                            'hover:border-accent-500 hover:shadow-card hover:-translate-y-0.5 ' +
                                            'focus-visible:outline focus-visible:outline-2 ' +
                                            'focus-visible:outline-offset-2 focus-visible:outline-accent-500 ' +
                                            cardBase
                                        }
                                    >
                                        {body}
                                    </a>
                                ) : (
                                    <div key={key} className={cardBase}>
                                        {body}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </Container>
        </AppShell>
    );
}

export default SupportPage;
